import React, { useRef, useState, useEffect, useMemo } from "react";
import { motion, useMotionValue, useSpring, AnimatePresence } from "framer-motion";
import { 
  FaUser, FaArrowRight, FaBookOpen, FaPenFancy, 
  FaEdit, FaTrash, FaSearch, FaCrown, FaQuoteLeft, FaOm, FaTimes,
  FaChevronDown, FaSun, FaLeaf, FaSpa
} from "react-icons/fa";
import { GiLotus } from "react-icons/gi";
import { FiClock, FiCheckCircle, FiCheck, FiX } from "react-icons/fi";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { blogAPI } from "../services/api";
import { isAuthenticated } from "../utils/auth";

// Hardware acceleration helper (optimized for smooth performance)
const gpu = { willChange: "transform, opacity", transform: "translateZ(0)", backfaceVisibility: "hidden" };

// Stagger Animation Variants
const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.1 }
  }
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } }
};

// 3D Magnetic Luxury Card
function LuxuryMagCard({ children, className = "", style = {} }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 20 });
  const sy = useSpring(y, { stiffness: 180, damping: 20 });

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    x.set(((e.clientX - rect.left) / rect.width - 0.5) * 10);
    y.set(((e.clientY - rect.top) / rect.height - 0.5) * -10);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX: sy, rotateY: sx, transformStyle: "preserve-3d", transformPerspective: 1200, ...style }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const CATEGORIES = ["All", "Inspiration", "Guides", "Mindfulness", "Nutrition", "Sadhana"];

const CATEGORY_OPTIONS = [
  { value: "Inspiration", label: "Inspiration", Icon: FaSun, desc: "Philosophical insights & heart openings" },
  { value: "Guides", label: "Guides", Icon: FaBookOpen, desc: "Step-by-step posture & sequence guides" },
  { value: "Mindfulness", label: "Mindfulness", Icon: FaSpa, desc: "Dhyana, breathwork & mental stillness" },
  { value: "Nutrition", label: "Nutrition", Icon: FaLeaf, desc: "Ayurvedic diet & sattvic living" },
  { value: "Sadhana", label: "Sadhana", Icon: FaOm, desc: "Sacred daily spiritual practice" }
];

function CustomCategorySelect({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOpt =
    CATEGORY_OPTIONS.find((c) => c.value.toLowerCase() === (value || "").toLowerCase()) ||
    CATEGORY_OPTIONS[0];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-[54px] rounded-[18px] border transition-all duration-300 px-5 flex items-center justify-between cursor-pointer text-left shadow-inner ${
          isOpen
            ? "border-[#F29A0C] bg-white ring-2 ring-[#F29A0C]/20 shadow-[0_4px_20px_rgba(242,154,12,0.15)]"
            : "border-[#F4D9B5] bg-[#FFF8EE] hover:border-[#F29A0C]/70 hover:bg-white"
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-[#FFF3DF] border border-[#F4D9B5] flex items-center justify-center text-[#F29A0C] text-sm shrink-0">
            <selectedOpt.Icon />
          </div>
          <div>
            <span className="text-sm font-bold text-[#111111]">{selectedOpt.label}</span>
          </div>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="text-[#F29A0C] text-xs"
        >
          <FaChevronDown />
        </motion.div>
      </button>

      {/* Custom Dropdown Popover */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.98 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute left-0 right-0 top-[60px] z-50 bg-white/98 backdrop-blur-xl border-2 border-[#F4D9B5] rounded-[22px] p-2 shadow-[0_20px_50px_rgba(242,154,12,0.22)] overflow-hidden"
          >
            <div className="px-3 py-2 text-[10.5px] font-bold uppercase tracking-wider text-[#F29A0C] border-b border-[#F4D9B5]/40 mb-1 flex items-center justify-between">
              <span>Select Category</span>
              <span className="text-[#888888] font-normal lowercase">5 topics</span>
            </div>
            <div className="space-y-1">
              {CATEGORY_OPTIONS.map((opt) => {
                const isSelected = opt.value.toLowerCase() === (value || "").toLowerCase();
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => {
                      onChange(opt.value);
                      setIsOpen(false);
                    }}
                    className={`w-full px-3.5 py-2.5 rounded-[14px] flex items-center justify-between transition-all duration-200 cursor-pointer border-none text-left ${
                      isSelected
                        ? "bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white shadow-md font-bold"
                        : "bg-transparent text-[#222222] hover:bg-[#FFF3DF] hover:text-[#111111]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 transition-colors ${
                        isSelected 
                          ? "bg-white/20 text-white" 
                          : "bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C]"
                      }`}>
                        <opt.Icon />
                      </div>
                      <div>
                        <div className={`text-xs sm:text-sm ${isSelected ? "text-white font-bold" : "text-[#111111] font-semibold"}`}>
                          {opt.label}
                        </div>
                        <div className={`text-[10px] ${isSelected ? "text-white/80" : "text-[#666666]"}`}>
                          {opt.desc}
                        </div>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-white text-xs">
                        <FiCheck />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Blog() {
  const canWriteBlog = isAuthenticated();
  const [posts, setPosts] = useState([]);
  const [loadingBlogs, setLoadingBlogs] = useState(true);
  const [showWriteForm, setShowWriteForm] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [readingPostModal, setReadingPostModal] = useState(null);

  const [newPost, setNewPost] = useState({
    title: "",
    excerpt: "",
    category: "Inspiration",
    author: "",
    readTime: "",
    image: ""
  });

  const handleContributeClick = () => {
    setShowWriteForm(true);
    setTimeout(() => {
      const el = document.getElementById("scriptorium-write-section");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Fetch blogs from DB on mount
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await blogAPI.getBlogs();
        if (res.data && res.data.success) {
          const formatted = res.data.data.map((post) => {
            const dateVal = post.createdAt || post.CreatedAt || new Date().toISOString();
            return {
              ...post,
              id: post.id || post.Id,
              title: post.title || post.Title,
              excerpt: post.excerpt || post.Excerpt,
              category: post.category || post.Category || "Inspiration",
              author: post.author || post.Author || "Mrs. Divya Nithin Bhojwani",
              readTime: post.readTime || post.ReadTime || "5 Min Read",
              image: post.image || post.Image || "/assets/course_hatha_yoga.jpg",
              date: new Date(dateVal).toLocaleDateString("en-US", {
                month: "short",
                day: "2-digit",
                year: "numeric"
              })
            };
          });
          setPosts(formatted);
        }
      } catch (err) {
        console.error("Failed to fetch blogs:", err);
      } finally {
        setLoadingBlogs(false);
      }
    };
    fetchBlogs();
  }, []);

  // Category counts
  const categoryCounts = useMemo(() => {
    const counts = { All: posts.length };
    CATEGORIES.forEach((cat) => {
      if (cat !== "All") {
        counts[cat] = posts.filter((p) => (p.category || "").toLowerCase() === cat.toLowerCase()).length;
      }
    });
    return counts;
  }, [posts]);

  // Filtered posts based on active category & real-time search
  const filteredPosts = useMemo(() => {
    return posts.filter((post) => {
      const matchCat =
        selectedCategory === "All" ||
        (post.category || "").toLowerCase() === selectedCategory.toLowerCase();
      const matchSearch =
        !searchQuery.trim() ||
        (post.title || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.excerpt || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (post.author || "").toLowerCase().includes(searchQuery.toLowerCase());
      return matchCat && matchSearch;
    });
  }, [posts, selectedCategory, searchQuery]);

  const handleStartEdit = (post, e) => {
    e.stopPropagation();
    setEditingPostId(post.id);
    setNewPost({
      title: post.title,
      excerpt: post.excerpt,
      category: post.category,
      author: post.author,
      readTime: post.readTime,
      image: post.image
    });
    setShowWriteForm(true);
    window.scrollTo({ top: 500, behavior: "smooth" });
  };

  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    postId: null,
    title: "",
    isDeleting: false
  });

  const handleDelete = (id, e) => {
    if (e) e.stopPropagation();
    const post = posts.find((p) => p.id === id);
    setDeleteModalState({
      isOpen: true,
      postId: id,
      title: post?.title || "this article",
      isDeleting: false
    });
  };

  const handleConfirmDelete = async () => {
    const id = deleteModalState.postId;
    if (!id) return;
    setDeleteModalState((prev) => ({ ...prev, isDeleting: true }));
    try {
      await blogAPI.deleteBlog(id);
      setPosts((prev) => prev.filter((p) => p.id !== id));
      if (readingPostModal?.id === id) setReadingPostModal(null);
      toast.success("Article deleted successfully!");
    } catch (err) {
      toast.error("Failed to delete article");
    } finally {
      setDeleteModalState({ isOpen: false, postId: null, title: "", isDeleting: false });
    }
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    
    const payload = {
      title: newPost.title,
      excerpt: newPost.excerpt,
      category: newPost.category,
      author: newPost.author || "Mrs. Divya Nithin Bhojwani",
      readTime: newPost.readTime || "5 Min Read",
      image: newPost.image || "/assets/course_hatha_yoga.jpg"
    };

    try {
      if (editingPostId) {
        const res = await blogAPI.updateBlog(editingPostId, payload);
        if (res.data && res.data.success) {
          const updatedPost = res.data.data;
          const dateVal = updatedPost.createdAt || updatedPost.CreatedAt || new Date().toISOString();
          const formattedPost = {
            ...updatedPost,
            id: updatedPost.id || updatedPost.Id,
            title: updatedPost.title || updatedPost.Title,
            excerpt: updatedPost.excerpt || updatedPost.Excerpt,
            category: updatedPost.category || updatedPost.Category,
            author: updatedPost.author || updatedPost.Author || "Mrs. Divya Nithin Bhojwani",
            readTime: updatedPost.readTime || updatedPost.ReadTime || "5 Min Read",
            image: updatedPost.image || updatedPost.Image || "/assets/course_hatha_yoga.jpg",
            date: new Date(dateVal).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric"
            })
          };
          setPosts((prev) => prev.map((p) => (p.id === editingPostId ? formattedPost : p)));
          setEditingPostId(null);
          setNewPost({ title: "", excerpt: "", category: "Inspiration", author: "", readTime: "", image: "" });
          setShowWriteForm(false);
          toast.success("Article updated successfully!");
        }
      } else {
        const res = await blogAPI.createBlog(payload);
        if (res.data && res.data.success) {
          const createdPost = res.data.data;
          const dateVal = createdPost.createdAt || createdPost.CreatedAt || new Date().toISOString();
          const formattedPost = {
            ...createdPost,
            id: createdPost.id || createdPost.Id,
            title: createdPost.title || createdPost.Title,
            excerpt: createdPost.excerpt || createdPost.Excerpt,
            category: createdPost.category || createdPost.Category,
            author: createdPost.author || createdPost.Author || "Mrs. Divya Nithin Bhojwani",
            readTime: createdPost.readTime || createdPost.ReadTime || "5 Min Read",
            image: createdPost.image || createdPost.Image || "/assets/course_hatha_yoga.jpg",
            date: new Date(dateVal).toLocaleDateString("en-US", {
              month: "short",
              day: "2-digit",
              year: "numeric"
            })
          };
          setPosts((prev) => [formattedPost, ...prev]);
          setNewPost({ title: "", excerpt: "", category: "Inspiration", author: "", readTime: "", image: "" });
          setShowWriteForm(false);
          toast.success("Article published successfully!");
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save article");
    }
  };

  // Hero Spotlight Article (Featured post: latest or 1st item)
  const spotlightPost = filteredPosts.length > 0 ? filteredPosts[0] : null;

  return (
    <div className="relative overflow-hidden bg-[#FFF8EE] min-h-screen">

      {/* ══════════════════════════════════════════════════════════════════════════
          1. HERO — FULL VIEWPORT OCCUPATION (DYNAMIC 100DVH / 100VW)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="relative w-full min-h-screen min-h-[100dvh] flex items-center justify-center overflow-hidden bg-[#140802]">
        
        {/* Sacred Himalayan Ashram Scriptorium Photographic Backdrop */}
        <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
          <img 
            src="/assets/blog_hero_bg.jpg" 
            alt="Sacred Himalayan Vedic Ashram Scriptorium" 
            className="w-full h-full object-cover object-center scale-[1.03]"
            style={{ filter: "brightness(0.74) saturate(1.15)" }}
          />
        </div>

        {/* ── OVERLAPPING WARM ESPRESSO BROWN, SAFFRON ORANGE & SACRED OLIVE GREEN TINTS ── */}
        <div 
          className="absolute inset-0 pointer-events-none z-[1]"
          style={{
            background: "linear-gradient(135deg, rgba(22, 10, 3, 0.58) 0%, rgba(242, 154, 12, 0.24) 38%, rgba(35, 15, 3, 0.52) 70%, rgba(58, 72, 46, 0.60) 100%)"
          }}
        />

        {/* Golden Sunburst Radiance Glow */}
        <div 
          className="absolute top-[22%] left-1/2 -translate-x-1/2 w-[880px] h-[560px] pointer-events-none rounded-full z-[1]"
          style={{
            background: "radial-gradient(circle, rgba(242, 154, 12, 0.32) 0%, rgba(255, 180, 0, 0.14) 45%, transparent 72%)",
            filter: "blur(60px)",
          }}
        />

        {/* Sacred Olive Green Glowing Aura Orb */}
        <motion.div
          animate={{ scale: [1, 1.15, 1], opacity: [0.20, 0.36, 0.20] }}
          transition={{ repeat: Infinity, duration: 10, ease: "easeInOut" }}
          className="absolute top-[-200px] right-[-140px] w-[750px] h-[750px] rounded-full pointer-events-none will-change-opacity z-[1]"
          style={{ background: "radial-gradient(circle, rgba(107, 138, 90, 0.30) 0%, transparent 70%)", ...gpu }}
        />

        {/* Warm Saffron Amber Ambient Orb */}
        <motion.div
          animate={{ scale: [1, 1.18, 1], opacity: [0.18, 0.32, 0.18] }}
          transition={{ repeat: Infinity, duration: 12, ease: "easeInOut", delay: 1 }}
          className="absolute bottom-[8%] left-[-100px] w-[650px] h-[650px] rounded-full pointer-events-none will-change-opacity z-[1]"
          style={{ background: "radial-gradient(circle, rgba(242, 154, 12, 0.24) 0%, transparent 70%)", ...gpu }}
        />

        {/* Floating Sacred Golden, Saffron & Olive Stardust Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-10">
          {[...Array(24)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                y: [0, -45, 0],
                x: [0, ((i * 7) % 26) - 13, 0],
                opacity: [0.3, 0.9, 0.3],
                scale: [1, 1.35, 1],
              }}
              transition={{
                duration: 4.5 + (i % 5) * 0.8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.25,
              }}
              className="absolute rounded-full"
              style={{
                width: `${3.5 + (i % 4) * 2}px`,
                height: `${3.5 + (i % 4) * 2}px`,
                backgroundColor: i % 3 === 0 ? "#FFD166" : i % 3 === 1 ? "#F29A0C" : "#9DC08B",
                boxShadow: "0 0 12px rgba(242, 154, 12, 0.7)",
                top: `${12 + (i * 7.5) % 75}%`,
                left: `${6 + (i * 8.5) % 88}%`,
              }}
            />
          ))}
        </div>

        {/* ── HERO BOTTOM TRANSITION: WARM ESPRESSO BROWN, SAFFRON ORANGE & OLIVE GREEN OVERLAP ── */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none z-10"
          style={{
            background: "linear-gradient(to bottom, transparent 0%, rgba(32, 14, 4, 0.35) 30%, rgba(242, 154, 12, 0.25) 60%, rgba(68, 88, 54, 0.65) 88%, #FFF8EE 100%)"
          }}
        />

        {/* Radiant Accent Seam: Olive Green & Saffron Orange Edge Glow */}
        <div 
          className="absolute bottom-0 inset-x-0 h-[2.5px] pointer-events-none opacity-80 z-10"
          style={{
            background: "linear-gradient(90deg, transparent 0%, #4D6638 20%, #F29A0C 50%, #688A54 80%, transparent 100%)"
          }}
        />

        {/* Hero Content, dynamically centered and responsive across all devices */}
        <motion.div 
          className="relative z-20 w-full max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-12 text-center pt-28 pb-20 sm:pt-32 sm:pb-24 flex flex-col items-center justify-center"
          style={gpu}
        >
          <div className="max-w-4xl mx-auto space-y-6 sm:space-y-7">
            
            {/* Top Royal Seal Badge */}
            <motion.div 
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="inline-flex items-center gap-3 rounded-full border border-[#F29A0C]/50 bg-black/60 backdrop-blur-2xl px-6 py-2.5 shadow-[0_10px_30px_rgba(0,0,0,0.5)]"
            >
              <GiLotus className="text-base text-[#FFD166] animate-pulse" />
              <span className="text-[11px] sm:text-xs tracking-[3px] uppercase font-bold text-white">
                DIVYYOG FOUNDATION WELLNESS JOURNAL & VEDIC INSIGHTS
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 25 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              style={{ fontFamily: "'Poppins', sans-serif", ...gpu }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[1.12] tracking-tight text-white drop-shadow-[0_4px_25px_rgba(0,0,0,0.95)]"
            >
              <span>Illumine Your Mind &</span>
              <span 
                className="block mt-1 sm:mt-2 bg-gradient-to-r from-[#FFF2B2] via-[#FFD166] to-[#F29A0C] bg-clip-text text-transparent"
                style={{ filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.95)) drop-shadow(0 0 35px rgba(242,154,12,0.65))" }}
              >
                Sacred Sadhana
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              style={gpu}
              className="text-white/90 text-base sm:text-lg md:text-xl font-medium leading-[1.75] max-w-3xl mx-auto drop-shadow-[0_2px_12px_rgba(0,0,0,0.85)]"
            >
              Authentic teachings, pranayama secrets, mindfulness rituals, and Ayurvedic lifestyle guidelines curated by Mrs. Divya Nithin Bhojwani and certified acharyas.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              style={gpu}
              className="flex flex-wrap items-center justify-center gap-4 pt-2"
            >
              <a href="#articles-grid" className="no-underline">
                <motion.button
                  whileHover={{ y: -4, scale: 1.03 }}
                  style={gpu}
                  className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF7A00] text-white px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider shadow-[0_12px_35px_rgba(242,154,12,0.45)] cursor-pointer flex items-center gap-2.5 border-none"
                >
                  <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-all duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12" />
                  <span className="relative z-20 flex items-center gap-2.5">
                    <span>Explore Journal Articles</span>
                    <FaArrowRight size={13} />
                  </span>
                </motion.button>
              </a>

              {canWriteBlog && (
                <motion.button 
                  whileHover={{ y: -4 }} 
                  style={gpu} 
                  onClick={() => {
                    setShowWriteForm(true);
                    window.scrollTo({ top: 750, behavior: "smooth" });
                  }}
                  className="rounded-full border border-white/30 bg-black/55 backdrop-blur-2xl text-white px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-wider hover:bg-black/80 hover:border-[#F29A0C]/60 transition-all cursor-pointer flex items-center gap-2.5"
                >
                  <FaPenFancy className="text-[#FFD166]" size={13} />
                  <span>Draft New Article</span>
                </motion.button>
              )}
            </motion.div>

          </div>
        </motion.div>

      </section>


      {/* ══════════════════════════════════════════════════════════════════════════
          2. CATEGORY FILTERS & REAL-TIME SEARCH BAR
      ══════════════════════════════════════════════════════════════════════════ */}
      <section id="articles-grid" className="relative z-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 -mt-8">
        
        <div className="p-6 sm:p-8 rounded-[32px] bg-white/95 border-2 border-[#F4D9B5] shadow-[0_20px_60px_rgba(242,154,12,0.1)] backdrop-blur-2xl space-y-6">
          
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 border-b border-[#F4D9B5]/60 pb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#111111] leading-tight tracking-[0.02em]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Vedic Journal & Wisdom Feed
              </h2>
              <p className="text-xs sm:text-sm text-[#666666] font-medium mt-1">
                Deepen your understanding of classical yoga, meditation, mindful living, and holistic wellness.
              </p>
            </div>

            {/* Action Toolbar: Search + Prominent Write Article Button */}
            <div className="flex flex-wrap items-center gap-3 w-full lg:w-auto">
              
              {/* Real-time Search Input */}
              <div className="relative flex items-center flex-1 sm:w-[320px] h-[50px] rounded-full border border-[#F4D9B5] bg-[#FFF8EE] px-4 focus-within:border-[#F29A0C] focus-within:bg-white transition-all shadow-inner">
                <FaSearch className="text-[#F29A0C] text-sm shrink-0" />
                <input 
                  type="text"
                  placeholder="Search articles, wisdom, authors…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-transparent border-none outline-none text-xs sm:text-sm text-[#111111] font-semibold px-3 placeholder:text-[#888888]"
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="text-[#999999] hover:text-[#111111] text-xs p-1 bg-transparent border-none cursor-pointer"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* HIGH VISIBILITY WRITE ARTICLE BUTTON */}
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => {
                  setShowWriteForm(true);
                  setTimeout(() => {
                    const el = document.getElementById("scriptorium-write-section");
                    if (el) el.scrollIntoView({ behavior: "smooth" });
                  }, 80);
                }}
                className="h-[50px] px-6 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] hover:from-[#E08500] hover:to-[#F29A0C] text-white text-xs sm:text-sm font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all flex items-center gap-2.5 cursor-pointer border-none shrink-0"
              >
                <FaPenFancy size={13} />
                <span>Write an Article</span>
              </motion.button>

              <div className="text-xs font-bold text-[#F29A0C] bg-[#FFF8EE] px-4 py-3 rounded-full border border-[#F4D9B5] shrink-0 whitespace-nowrap">
                {filteredPosts.length} Articles
              </div>
            </div>
          </div>

          {/* Filter Pills */}
          <div className="ud-scroll ud-scroll-pad flex flex-wrap gap-2.5">
            {CATEGORIES.map((cat) => {
              const active = selectedCategory.toLowerCase() === cat.toLowerCase();
              const count = categoryCounts[cat] || 0;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-3 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 cursor-pointer flex items-center gap-2.5 border-none outline-none ${
                    active
                      ? "bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white shadow-[0_8px_25px_rgba(242,154,12,0.35)] scale-105"
                      : "bg-[#FFF8EE] text-[#444444] hover:text-[#111111] border border-[#F4D9B5] hover:bg-white"
                  }`}
                >
                  <span>{cat}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-extrabold ${
                    active ? "bg-black/25 text-white" : "bg-white text-[#F29A0C] border border-[#F4D9B5]"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

        </div>

      </section>


      {/* ══════════════════════════════════════════════════════════════════════════
          3. FEATURED SPOTLIGHT ARTICLE (HERO ARTICLE CARD)
      ══════════════════════════════════════════════════════════════════════════ */}
      {spotlightPost && selectedCategory === "All" && !searchQuery && (
        <section className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-14">
          <LuxuryMagCard>
            <div 
              onClick={() => setReadingPostModal(spotlightPost)}
              className="rounded-[36px] sm:rounded-[44px] border-2 border-[#F4D9B5] bg-white p-6 sm:p-10 shadow-[0_20px_60px_rgba(242,154,12,0.1)] hover:border-[#F29A0C] transition-all duration-500 cursor-pointer overflow-hidden group relative"
            >
              <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-10 items-center">
                
                {/* Spotlight Image Container (6 Columns) */}
                <div className="lg:col-span-6 relative rounded-[28px] overflow-hidden h-[300px] sm:h-[380px] w-full bg-[#1A0E05] shadow-lg group/spot">
                  <img 
                    src={spotlightPost.image} 
                    alt={spotlightPost.title} 
                    className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover/spot:scale-108 select-none"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/25 pointer-events-none" />

                  {/* Top Left Spotlight Tag */}
                  <div className="absolute top-4 left-4 z-20 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-[11px] font-extrabold uppercase tracking-wider shadow-md">
                    <FaCrown size={12} className="text-[#FFD166]" />
                    <span>Featured Article</span>
                  </div>

                  {/* Top Right Category */}
                  <div className="absolute top-4 right-4 z-20 px-3.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/25 text-white text-[10.5px] font-bold uppercase tracking-wider shadow-md">
                    {spotlightPost.category}
                  </div>

                  {/* Bottom Badges */}
                  <div className="absolute bottom-4 left-4 right-4 z-20 flex items-center justify-between text-xs text-white">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-[11px] font-bold">
                      <FiClock className="text-[#FFD166]" />
                      <span>{spotlightPost.readTime}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-[11px] font-bold text-white/90">
                      <span>{spotlightPost.date}</span>
                    </span>
                  </div>
                </div>

                {/* Spotlight Content (6 Columns) */}
                <div className="lg:col-span-6 space-y-5">
                  <div className="inline-flex items-center gap-2.5 px-3 py-1 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-xs font-bold text-[#6B8A5A]">
                    <FiCheckCircle />
                    <span>Master Acharya Editorial</span>
                  </div>

                  <h3 
                    className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111111] leading-tight tracking-[0.02em] group-hover:text-[#F29A0C] transition-colors"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {spotlightPost.title}
                  </h3>

                  <p className="text-sm sm:text-base text-[#555555] leading-relaxed line-clamp-4 font-normal">
                    {spotlightPost.excerpt}
                  </p>

                  <div className="pt-2 flex items-center justify-between border-t border-[#F4D9B5]/60">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white flex items-center justify-center font-bold text-sm shadow-md">
                        {spotlightPost.author.charAt(0)}
                      </div>
                      <div>
                        <h4 className="text-xs sm:text-sm font-bold text-[#111111]">{spotlightPost.author}</h4>
                        <p className="text-[11px] text-[#777777] font-medium">Divyyog Foundation Faculty</p>
                      </div>
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="px-6 py-3 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all flex items-center gap-2 cursor-pointer border-none"
                    >
                      <span>Read Article</span>
                      <FaArrowRight size={11} />
                    </motion.button>
                  </div>

                </div>

              </div>
            </div>
          </LuxuryMagCard>
        </section>
      )}


      {/* ══════════════════════════════════════════════════════════════════════════
          5. MAIN ARTICLE GRID
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-16 px-4 sm:px-6 lg:px-12 max-w-[1440px] mx-auto">
        
        {loadingBlogs ? (
          <div className="flex flex-col justify-center items-center py-28 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F29A0C]"></div>
            <p className="text-xs font-bold text-[#888888] uppercase tracking-widest">Loading Vedic Archives…</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-[650px] mx-auto text-center py-20 px-8 rounded-[38px] border border-[#F4D9B5] bg-white/70 backdrop-blur-2xl shadow-[0_20px_50px_rgba(0,0,0,0.04)]"
          >
            <div className="w-16 h-16 rounded-full bg-[#F29A0C]/10 border border-[#F29A0C]/25 flex items-center justify-center text-[#F29A0C] mx-auto mb-6 text-2xl shadow-inner">
              <FaBookOpen />
            </div>
            <h3 className="text-2xl font-bold text-[#111111] mb-2 font-poppins">No Articles Match Your Search</h3>
            <p className="text-[#666666] text-sm leading-relaxed mb-6 font-medium">
              We couldn't find any articles matching "{searchQuery}". Try browsing a different category or clearing your search.
            </p>
            <button
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("All");
              }}
              className="px-8 py-3.5 rounded-full bg-[#F29A0C] hover:bg-[#FF8A00] text-white text-xs font-bold tracking-wider uppercase transition-all shadow-md cursor-pointer border-none"
            >
              Reset Filters
            </button>
          </motion.div>
        ) : (
          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            animate="show"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-10"
          >
            {filteredPosts.map((post, idx) => (
              <motion.div key={post.id || idx} variants={fadeInUp} className="flex">
                <LuxuryMagCard className="w-full flex">
                  <div 
                    onClick={() => setReadingPostModal(post)}
                    className="w-full rounded-[36px] bg-white border-2 border-[#F4D9B5] shadow-[0_20px_50px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(242,154,12,0.18)] hover:border-[#F29A0C] transition-all duration-500 overflow-hidden flex flex-col justify-between p-6 sm:p-7 relative group cursor-pointer"
                  >
                    {/* Top Radiant Shimmer Line */}
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#F29A0C] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                    <div className="space-y-6">
                      
                      {/* ── LUXURY FULL-BLEED IMAGE CONTAINER (SEAMLESS EDGE-TO-EDGE) ── */}
                      <div className="relative rounded-[24px] overflow-hidden h-[240px] sm:h-[260px] w-full bg-[#1A0E05] shadow-md group/img">
                        <img 
                          src={post.image} 
                          alt={post.title} 
                          className="w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover/img:scale-108 select-none"
                          loading="lazy" 
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/25 pointer-events-none" />

                        {/* Category Tag */}
                        <div className="absolute top-3.5 left-3.5 z-20 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-[10.5px] font-extrabold uppercase tracking-wider shadow-md">
                          {post.category}
                        </div>

                        {/* Read Time & Date Badge */}
                        <div className="absolute bottom-3 left-3.5 right-3.5 z-20 flex items-center justify-between text-xs text-white">
                          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-[11px] font-bold shadow-sm">
                            <FiClock className="text-[#FFD166]" />
                            <span>{post.readTime}</span>
                          </div>
                          <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white text-[11px] font-extrabold shadow-sm">
                            <span>{post.date}</span>
                          </div>
                        </div>

                        {/* Action buttons (Edit & Delete) for authorized members */}
                        {canWriteBlog && (
                          <div className="absolute top-3.5 right-3.5 flex items-center gap-2 z-30">
                            <button 
                              onClick={(e) => handleStartEdit(post, e)}
                              className="w-8 h-8 rounded-full bg-black/60 hover:bg-[#F29A0C] text-white border border-white/30 backdrop-blur-md flex items-center justify-center transition-all shadow-md cursor-pointer border-none"
                              title="Edit Article"
                            >
                              <FaEdit size={12} />
                            </button>
                            <button 
                              onClick={(e) => handleDelete(post.id, e)}
                              className="w-8 h-8 rounded-full bg-black/60 hover:bg-red-600 text-white border border-white/30 backdrop-blur-md flex items-center justify-center transition-all shadow-md cursor-pointer border-none"
                              title="Delete Article"
                            >
                              <FaTrash size={12} />
                            </button>
                          </div>
                        )}
                      </div>

                      {/* ── ARTICLE DETAILS ── */}
                      <div className="space-y-3">
                        {/* Author Profile Chip */}
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C] flex items-center justify-center font-bold text-xs shadow-sm">
                            <FaUser size={10} />
                          </div>
                          <span className="text-xs font-bold text-[#333333]">{post.author}</span>
                        </div>

                        <h3 
                          className="text-xl sm:text-2xl font-bold text-[#111111] leading-snug tracking-[0.02em] group-hover:text-[#F29A0C] transition-colors line-clamp-2"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {post.title}
                        </h3>

                        <p className="text-xs sm:text-sm text-[#666666] leading-relaxed line-clamp-3 font-normal">
                          {post.excerpt}
                        </p>
                      </div>

                    </div>

                    {/* Bottom Read Action */}
                    <div className="pt-6 border-t border-[#F4D9B5]/70 mt-6">
                      <button 
                        onClick={() => setReadingPostModal(post)}
                        className="w-full py-3.5 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-xs font-bold uppercase tracking-wider text-[#111111] group-hover:bg-gradient-to-r group-hover:from-[#F29A0C] group-hover:to-[#FF8400] group-hover:text-white group-hover:border-transparent transition-all duration-300 flex items-center justify-center gap-2 shadow-sm cursor-pointer"
                      >
                        <span>Read Full Article</span>
                        <FaArrowRight size={11} className="group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>

                  </div>
                </LuxuryMagCard>
              </motion.div>
            ))}
          </motion.div>
        )}

      </section>


      {/* ══════════════════════════════════════════════════════════════════════════
          5. ARTICLE WRITING & COMMUNITY REFLECTION SANCTUM (BELOW BLOG CARDS)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section id="scriptorium-write-section" className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-12 pt-6 pb-12">
        
        {/* If write form is collapsed, show an inviting, prominent trigger card */}
        {!showWriteForm ? (
          <div className="rounded-[32px] border-2 border-dashed border-[#F4D9B5] bg-gradient-to-r from-[#FFFDF9] via-[#FFF8EE] to-[#FFFDF9] p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm hover:border-[#F29A0C] transition-all">
            <div className="flex items-center gap-4 text-center md:text-left">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F29A0C] to-[#FF8400] text-white flex items-center justify-center text-xl shadow-md shrink-0">
                <FaPenFancy />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Have an Insight or Sadhana Reflection to Share?
                </h3>
                <p className="text-xs sm:text-sm text-[#666666] font-medium mt-1 max-w-xl">
                  Contribute your perspective on asana alignment, breathing techniques, or classical yogic philosophy to inspire seekers worldwide.
                </p>
              </div>
            </div>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => {
                setShowWriteForm(true);
              }}
              className="px-8 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs sm:text-sm font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all flex items-center gap-2.5 cursor-pointer border-none shrink-0"
            >
              <FaPenFancy size={13} />
              <span>Write &amp; Publish Article</span>
            </motion.button>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="rounded-[36px] border-2 border-[#F4D9B5] bg-white p-6 sm:p-10 lg:p-12 shadow-[0_25px_70px_rgba(242,154,12,0.12)] relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 right-0 h-[4px] bg-gradient-to-r from-[#F29A0C] via-[#FFD166] to-[#F29A0C]" />
            
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#F4D9B5]/60">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] text-[#F29A0C] flex items-center justify-center text-xl shadow-sm">
                  <FaPenFancy />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#111111]" style={{ fontFamily: "'Poppins', sans-serif" }}>
                    {editingPostId ? "Edit Wellness Article" : "Publish to the Wellness Journal"}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#666666] font-medium mt-0.5">
                    Share authentic yoga techniques, anatomy tips, or spiritual insights with thousands of global practitioners.
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setShowWriteForm(false);
                  setEditingPostId(null);
                }}
                className="w-10 h-10 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] flex items-center justify-center transition-colors cursor-pointer border-none"
              >
                <FiX size={18} />
              </button>
            </div>

            {!canWriteBlog && (
              <div className="mb-6 p-4 rounded-2xl bg-[#FFF8EE] border border-[#F4D9B5] flex flex-col sm:flex-row items-center justify-between gap-3">
                <p className="text-xs sm:text-sm text-[#444444] font-medium">
                  <strong>Guest Note:</strong> You can draft your article below. To publish live to the academy feed, please sign in.
                </p>
                <Link to="/login">
                  <button className="px-5 py-2 rounded-full bg-[#F29A0C] text-white text-xs font-bold uppercase tracking-wider border-none cursor-pointer">
                    Sign In to Publish
                  </button>
                </Link>
              </div>
            )}

            <form onSubmit={handlePublish} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Article Title</label>
                  <div className="h-[54px] rounded-[18px] border border-[#F4D9B5] bg-[#FFF8EE] px-5 focus-within:bg-white focus-within:border-[#F29A0C] transition-all flex items-center shadow-inner">
                    <input 
                      type="text" 
                      required 
                      placeholder="e.g. The Science of Pranic Breathing (Pranayama)"
                      value={newPost.title}
                      onChange={(e) => setNewPost(p => ({ ...p, title: e.target.value }))}
                      className="w-full bg-transparent border-none outline-none text-sm text-[#111111] font-semibold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Category</label>
                  <CustomCategorySelect
                    value={newPost.category}
                    onChange={(val) => setNewPost((p) => ({ ...p, category: val }))}
                  />
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                
                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Author Name</label>
                  <div className="h-[54px] rounded-[18px] border border-[#F4D9B5] bg-[#FFF8EE] px-5 focus-within:bg-white focus-within:border-[#F29A0C] transition-all flex items-center shadow-inner">
                    <input 
                      type="text" 
                      required 
                      placeholder="e.g. Mrs. Divya Nithin Bhojwani"
                      value={newPost.author}
                      onChange={(e) => setNewPost(p => ({ ...p, author: e.target.value }))}
                      className="w-full bg-transparent border-none outline-none text-sm text-[#111111] font-semibold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Estimated Read Time</label>
                  <div className="h-[54px] rounded-[18px] border border-[#F4D9B5] bg-[#FFF8EE] px-5 focus-within:bg-white focus-within:border-[#F29A0C] transition-all flex items-center shadow-inner">
                    <input 
                      type="text" 
                      required 
                      placeholder="e.g. 5 Min Read"
                      value={newPost.readTime}
                      onChange={(e) => setNewPost(p => ({ ...p, readTime: e.target.value }))}
                      className="w-full bg-transparent border-none outline-none text-sm text-[#111111] font-semibold"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Cover Image URL (Optional)</label>
                  <div className="h-[54px] rounded-[18px] border border-[#F4D9B5] bg-[#FFF8EE] px-5 focus-within:bg-white focus-within:border-[#F29A0C] transition-all flex items-center shadow-inner">
                    <input 
                      type="url" 
                      placeholder="https://... or /assets/..."
                      value={newPost.image}
                      onChange={(e) => setNewPost(p => ({ ...p, image: e.target.value }))}
                      className="w-full bg-transparent border-none outline-none text-sm text-[#111111] font-semibold"
                    />
                  </div>
                </div>

              </div>

              <div className="space-y-2">
                <label className="text-xs font-bold text-[#F29A0C] uppercase tracking-wider pl-1">Article Content &amp; Reflections</label>
                <div className="rounded-[22px] border border-[#F4D9B5] bg-[#FFF8EE] p-5 focus-within:bg-white focus-within:border-[#F29A0C] transition-all shadow-inner">
                  <textarea 
                    rows={6}
                    required 
                    placeholder="Write your article reflections, posture advice, breathing tips, or philosophy here..."
                    value={newPost.excerpt}
                    onChange={(e) => setNewPost(p => ({ ...p, excerpt: e.target.value }))}
                    className="w-full bg-transparent border-none outline-none text-sm text-[#111111] font-medium resize-none leading-relaxed"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowWriteForm(false);
                    setEditingPostId(null);
                  }}
                  className="px-6 py-3.5 rounded-full border border-[#F4D9B5] bg-[#FFF8EE] text-[#444444] text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors cursor-pointer"
                >
                  Cancel
                </button>

                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  type="submit"
                  className="px-10 py-4 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs tracking-wider uppercase font-bold shadow-lg hover:shadow-xl cursor-pointer border-none"
                >
                  {editingPostId ? "Update Article" : "Publish Article to Community"}
                </motion.button>
              </div>

            </form>

          </motion.div>
        )}
      </section>


      {/* ══════════════════════════════════════════════════════════════════════════
          6. INTERACTIVE FULL ARTICLE DOSSIER & READER MODAL
      ══════════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {readingPostModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
            
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setReadingPostModal(null)}
              className="fixed inset-0 bg-black/75 backdrop-blur-md"
            />

            {/* Reading Dialog Card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 25 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 25 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white rounded-[36px] sm:rounded-[44px] border-2 border-[#F4D9B5] shadow-[0_30px_100px_rgba(0,0,0,0.45)] z-10 p-6 sm:p-12 text-[#111111]"
            >
              {/* Close Button */}
              <button
                onClick={() => setReadingPostModal(null)}
                className="absolute top-6 right-6 w-11 h-11 rounded-full bg-[#FFF8EE] border border-[#F4D9B5] text-[#111111] hover:text-[#F29A0C] hover:border-[#F29A0C] flex items-center justify-center text-lg transition-all cursor-pointer z-20 shadow-sm border-none"
                aria-label="Close reader"
              >
                <FiX />
              </button>

              <div className="space-y-8">
                
                {/* Header Cover Image */}
                <div className="relative rounded-[28px] overflow-hidden h-[280px] sm:h-[360px] w-full bg-[#1A0E05] shadow-lg">
                  <img 
                    src={readingPostModal.image} 
                    alt={readingPostModal.title} 
                    className="w-full h-full object-cover object-center select-none"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  
                  <div className="absolute top-5 left-5 z-10 px-4 py-1.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-bold uppercase tracking-wider shadow-md">
                    {readingPostModal.category}
                  </div>

                  <div className="absolute bottom-5 left-5 right-5 z-10 flex items-center justify-between text-xs text-white">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 font-bold">
                      <FiClock className="text-[#FFD166]" />
                      <span>{readingPostModal.readTime}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/20 font-bold">
                      <span>Published {readingPostModal.date}</span>
                    </span>
                  </div>
                </div>

                {/* Author Info & Article Title */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3.5">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white flex items-center justify-center font-bold text-base shadow-md">
                      {readingPostModal.author.charAt(0)}
                    </div>
                    <div>
                      <h4 className="text-base font-bold text-[#111111]">{readingPostModal.author}</h4>
                      <p className="text-xs text-[#666666] font-medium">Lead Acharya & Faculty Contributor</p>
                    </div>
                  </div>

                  <h2 
                    className="text-2xl sm:text-3xl lg:text-4xl font-black text-[#111111] leading-tight tracking-tight pt-2"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {readingPostModal.title}
                  </h2>
                </div>

                {/* Article Body Content */}
                <div className="space-y-6 text-sm sm:text-base text-[#333333] leading-relaxed border-t border-b border-[#F4D9B5]/60 py-6">
                  <p className="text-base sm:text-lg font-medium text-[#222222] leading-relaxed italic border-l-4 border-[#F29A0C] pl-4 py-1 bg-[#FFF8EE]/60 rounded-r-xl">
                    &ldquo;{readingPostModal.excerpt}&rdquo;
                  </p>

                  <p className="leading-loose">
                    In classical yogic science, true physical wellness is never divorced from mental poise and pranic regulation. When we cultivate stillness in our posture (Asana) and synchronize the vital breath (Pranayama), the nervous system enters an optimal state of natural homeostasis and cellular regeneration.
                  </p>

                  <p className="leading-loose">
                    Whether embarking on your very first sun salutation or perfecting advanced inversions, the foundational secret remains unbroken awareness. As taught in the ancient Patanjali Yoga Sutras: <em>Sthira-sukham asanam</em> — posture must be firm, steady, yet filled with supreme ease and delight.
                  </p>
                </div>

                {/* Founder Philosophy Endnote */}
                <div className="p-6 rounded-[24px] bg-[#FFF8EE] border border-[#F4D9B5] flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-3 text-[#111111]">
                    <div className="w-10 h-10 rounded-full bg-[#F29A0C]/15 text-[#F29A0C] flex items-center justify-center text-lg flex-shrink-0">
                      <FaQuoteLeft />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-[#111111]">Taught under the vision of Mrs. Divya Nithin Bhojwani</p>
                      <p className="text-[11px] text-[#777777]">Founder, International Gold Medalist & Lead Acharya</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Link to="/courses" onClick={() => setReadingPostModal(null)}>
                      <button className="px-6 py-2.5 rounded-full bg-gradient-to-r from-[#F29A0C] to-[#FF8400] text-white text-xs font-bold uppercase tracking-wider shadow-md hover:shadow-lg transition-all cursor-pointer border-none">
                        Explore Courses
                      </button>
                    </Link>
                  </div>
                </div>

              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>


      {/* ══════════════════════════════════════════════════════════════════════════
          7. THE SACRED SCRIPTORIUM & VEDIC WISDOM SANCTUM (LUXURY ROYAL EDITORIAL)
      ══════════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-4 sm:px-6 lg:px-12 relative overflow-hidden bg-gradient-to-b from-[#FFF8EE] via-[#FFF3DF] to-[#FFF8EE]">
        
        {/* Ambient Warm Golden, Saffron & Olive Green Radiance in Background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] h-[650px] rounded-full bg-gradient-to-r from-[#F29A0C]/35 via-[#FFA41B]/28 to-[#6B8A5A]/32 blur-[140px] pointer-events-none" />

        <div className="max-w-[1360px] mx-auto relative z-10">
          <div className="relative">

            {/* Glowing Saffron & Golden Aura Rings */}
            <div className="absolute -inset-3 bg-gradient-to-r from-[#F29A0C] via-[#FF8400] to-[#6B8A5A] rounded-[48px] blur-2xl opacity-45 pointer-events-none" />
            <div className="absolute -inset-5 bg-gradient-to-r from-[#F29A0C]/40 via-[#FFA41B]/30 to-[#6B8A5A]/35 rounded-[56px] blur-3xl opacity-55 pointer-events-none" />

            <div
              className="relative overflow-hidden rounded-[36px] sm:rounded-[44px] border-2 border-[#F29A0C]/85 text-white p-8 sm:p-12 lg:p-16 shadow-[0_30px_90px_rgba(20,9,3,0.65)] backdrop-blur-3xl"
              style={{
                background: "linear-gradient(135deg, rgba(24, 11, 4, 0.96) 0%, rgba(242, 154, 12, 0.35) 35%, rgba(42, 19, 7, 0.92) 65%, rgba(61, 80, 46, 0.60) 100%)",
              }}
            >
              {/* Bespoke Scriptorium Twilight Photographic Backdrop */}
              <div
                className="absolute inset-0 bg-cover bg-center opacity-80 scale-[1.02] pointer-events-none"
                style={{ 
                  backgroundImage: "url('/assets/blog_bottom_sanctum.jpg')",
                  filter: "brightness(0.92) saturate(1.25)"
                }}
              />

              {/* Rich Overlapping Multi-Color Gradient (Warm Espresso + Vibrant Saffron + Sacred Olive Green) */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(135deg, rgba(20, 9, 3, 0.82) 0%, rgba(242, 154, 12, 0.36) 32%, rgba(35, 16, 5, 0.68) 60%, rgba(61, 80, 46, 0.54) 100%)"
                }}
              />

              {/* Overlapping Saffron Sunburst Glow in Top-Right */}
              <div 
                className="absolute -top-12 right-10 w-[520px] h-[380px] rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(242, 154, 12, 0.34) 0%, rgba(255, 132, 0, 0.16) 50%, transparent 75%)",
                  filter: "blur(45px)"
                }}
              />

              {/* Overlapping Sacred Olive Green Aura in Bottom-Left */}
              <div 
                className="absolute -bottom-16 -left-12 w-[520px] h-[380px] rounded-full pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(77, 102, 56, 0.38) 0%, rgba(107, 138, 90, 0.20) 50%, transparent 75%)",
                  filter: "blur(45px)"
                }}
              />

              {/* Subtle Golden Center Bloom */}
              <div 
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "radial-gradient(ellipse at 50% 25%, rgba(255, 209, 102, 0.15) 0%, transparent 65%)"
                }}
              />

              {/* Animated Rotating Sacred Om Watermark */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 75, repeat: Infinity, ease: "linear" }}
                className="absolute -right-20 -bottom-20 w-[380px] h-[380px] opacity-15 text-[#FFD166] pointer-events-none"
              >
                <FaOm className="w-full h-full" />
              </motion.div>

              {/* Top Golden-Orange Energy Shimmer Streak */}
              <div
                className="absolute top-0 left-0 right-0 h-[3.5px]"
                style={{
                  background: "linear-gradient(90deg, transparent, #F29A0C, #FFD166, #FF8400, #F29A0C, transparent)",
                }}
              />

              {/* Architectural Header */}
              <div className="relative z-10 text-center max-w-2xl mx-auto mb-14 sm:mb-16">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-black/50 border border-[#F29A0C]/50 backdrop-blur-md text-[#FFD166] text-[10.5px] sm:text-[11px] tracking-[0.28em] uppercase font-bold mb-3 shadow-sm">
                  <GiLotus className="text-sm text-[#F29A0C]" />
                  <span>DIVYAYOG SCRIPTORIUM &bull; EST. VEDIC LINEAGE</span>
                </div>

                <h2 
                  className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white tracking-tight leading-[1.15]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  The Living Scriptorium
                </h2>

                <p 
                  className="text-[#FFD166]/90 text-base sm:text-lg font-serif italic mt-3 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.7)]"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  Where classical Vedic philosophy meets the quiet contemplation of daily life.
                </p>
              </div>

              {/* Three Stately Editorial Folios */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10 items-stretch relative z-10">
                
                {/* Folio I: Classical Inquiries */}
                <div className="p-6 sm:p-7 rounded-[24px] bg-black/40 border border-white/15 backdrop-blur-xl flex flex-col justify-between shadow-lg">
                  <div>
                    <div className="text-[#FFD166] text-[10.5px] tracking-[0.25em] font-semibold uppercase mb-3">
                      FOLIO I &bull; SUTRA CONTEMPLATION
                    </div>
                    
                    <div 
                      className="text-2xl sm:text-3xl font-medium text-[#FFD166] mb-1"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      स्थिरसुखमासनम्
                    </div>
                    <div 
                      className="text-xs text-[#F4D9B5] font-serif italic mb-3"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      Sthira-sukham āsanam &bull; Yoga Sutra 2.46
                    </div>

                    <p className="text-xs sm:text-sm text-white/90 font-light leading-relaxed">
                      &ldquo;Posture is mastered when effort dissolves into natural stillness, and consciousness rests within the boundless.&rdquo; Reflect on maintaining steady balance throughout life&rsquo;s transitions.
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-white/10">
                    <button
                      type="button"
                      onClick={handleContributeClick}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[#FFD166] hover:text-[#FFA41B] transition-colors uppercase tracking-widest bg-transparent border-none p-0 cursor-pointer"
                    >
                      <span>Contribute a Reflection</span>
                      <FaArrowRight size={10} />
                    </button>
                  </div>
                </div>

                {/* Folio II: Sunday Morning Dispatch (The Sutra Scroll) */}
                <div className="p-6 sm:p-7 rounded-[24px] bg-black/45 border border-[#F29A0C]/45 backdrop-blur-xl flex flex-col justify-between relative overflow-hidden shadow-lg">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-[#F29A0C]/20 to-transparent pointer-events-none" />

                  <div>
                    <div className="text-[#FFD166] text-[10.5px] tracking-[0.25em] font-semibold uppercase mb-3">
                      FOLIO II &bull; SUNDAY DISPATCH
                    </div>

                    <h3 
                      className="text-2xl sm:text-3xl font-normal text-white mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      The Sutra Scroll
                    </h3>

                    <p className="text-xs sm:text-sm text-white/90 font-light leading-relaxed mb-4">
                      Curated every Sunday at dawn. Authentic philosophical commentary, anatomically safe posture breakdowns, and ayurvedic nutrition essays authored by our master faculty.
                    </p>

                    <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-xs text-[#F4D9B5] font-serif italic leading-relaxed">
                      &ldquo;The true journey of yoga is not physical strain, but the steady blossoming of inner stillness and radiant awareness.&rdquo;
                    </div>
                  </div>

                  <div className="pt-6 mt-6 border-t border-white/10 flex items-center justify-between">
                    <a
                      href="#articles-grid"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[#FFD166] hover:text-[#FFA41B] transition-colors uppercase tracking-widest no-underline"
                    >
                      <span>Explore Published Scrolls</span>
                      <FaArrowRight size={10} />
                    </a>
                  </div>
                </div>

                {/* Folio III: Academy Mentorship */}
                <div className="p-6 sm:p-7 rounded-[24px] bg-black/40 border border-white/15 backdrop-blur-xl flex flex-col justify-between shadow-lg">
                  <div>
                    <div className="text-[#FFD166] text-[10.5px] tracking-[0.25em] font-semibold uppercase mb-3">
                      FOLIO III &bull; ACADEMY LINEAGE
                    </div>

                    <h3 
                      className="text-2xl sm:text-3xl font-normal text-white mb-2"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      Living Mentorship
                    </h3>

                    <p className="text-xs sm:text-sm text-white/90 font-light leading-relaxed">
                      Bridge contemplative philosophy with rigorous live practice under the guidance of International Gold Medalist Mrs. Divya Nithin Bhojwani. Explore progressive masterclasses for every stage of sadhana.
                    </p>
                  </div>

                  <div className="pt-6 mt-6 border-t border-white/10">
                    <Link
                      to="/courses"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[#FFD166] hover:text-[#FFA41B] transition-colors uppercase tracking-widest no-underline"
                    >
                      <span>Explore Academy Courses</span>
                      <FaArrowRight size={10} />
                    </Link>
                  </div>
                </div>

              </div>

              {/* Bottom Anchor: Authentic Acharya Attribution & Quick Navigation */}
              <div className="mt-12 pt-8 border-t border-white/15 flex flex-col md:flex-row items-center justify-between gap-6 relative z-10">
                <div className="text-center md:text-left">
                  <p 
                    className="text-sm sm:text-base text-white/95 italic leading-relaxed"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    &ldquo;When contemplation is etched upon the page, the quiet mind reveals its boundless nature.&rdquo;
                  </p>
                  <p className="text-[11px] text-[#FFD166] uppercase tracking-wider font-medium mt-1">
                    — Mrs. Divya Nithin Bhojwani &bull; Founder & Lead Acharya
                  </p>
                </div>

                <div className="flex items-center gap-6 text-xs text-white/80">
                  <button
                    type="button"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className="hover:text-white transition-colors bg-transparent border-none cursor-pointer text-white/80 font-light flex items-center gap-1.5"
                  >
                    <span>Return to Top</span>
                    <FaArrowRight size={9} className="-rotate-90 text-[#FFD166]" />
                  </button>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* Custom Luxury Delete Confirmation Dialog */}
      <AnimatePresence>
        {deleteModalState.isOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !deleteModalState.isDeleting && setDeleteModalState({ isOpen: false, postId: null, title: "", isDeleting: false })}
              className="fixed inset-0 bg-black/65 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 16 }}
              className="relative w-full max-w-md bg-white rounded-[28px] border border-rose-200 shadow-2xl p-6 sm:p-7 z-10 text-left"
            >
              <div className="w-14 h-14 rounded-2xl bg-rose-50 border border-rose-200 flex items-center justify-center text-rose-600 mb-4 shadow-sm">
                <FaTrash size={20} />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#111111] mb-2" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Delete Article?
              </h3>
              <p className="text-xs sm:text-sm text-[#666666] leading-relaxed mb-6 font-medium">
                Are you sure you want to permanently delete <strong className="text-[#111111]">&ldquo;{deleteModalState.title}&rdquo;</strong>? This article will be permanently removed from the Scriptorium.
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  type="button"
                  disabled={deleteModalState.isDeleting}
                  onClick={() => setDeleteModalState({ isOpen: false, postId: null, title: "", isDeleting: false })}
                  className="px-5 py-2.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 text-xs font-bold transition-all cursor-pointer disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={deleteModalState.isDeleting}
                  onClick={handleConfirmDelete}
                  className="px-5 py-2.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold transition-all shadow-md hover:shadow-lg cursor-pointer flex items-center gap-2 disabled:opacity-50"
                >
                  {deleteModalState.isDeleting ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <FaTrash size={12} />
                      <span>Delete Article</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}
